import { observer } from 'mobx-react';
import React from 'react';
import { Badge, Button, Card, CardContent, Grid, GridColumn, Heading, Icon, Notice, Separator } from 'telia-front-react';
import UiNotice from '../components/UiNotice';
import Config from '../Config';
import useStores from '../stores/UseStores';
import { FetchStatus, PasskeyErrorType } from '../util/ApiUtil';
import WrapWithContainerInFullPage from '../wrapper/WrapWithContainerInFullPage';
import { PanelType } from './PanelTypes';

interface SelectedMobilePanelProps {
  panel: PanelType;
  setBackToMethodSelectionClicked: (clicked: boolean) => void;
}

const SelectedMobilePanel = observer((props: SelectedMobilePanelProps): React.JSX.Element => {
  const { panel, setBackToMethodSelectionClicked } = props;
  const {
    translateStore: { translate },
    homeStore: { panels },
    uiStore: { selectedTab, setSelectedTab, hasNotice, hideNotices },
    passkeyStore,
  } = useStores();
  const ChildComponent = panel.component;
  const isPasskeyPanel = panel.id === 'passkey';
  const passkeyExists = passkeyStore.getPasskeyExists();

  const handleBackToMethodSelection = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    event.preventDefault();
    hideNotices();
    setBackToMethodSelectionClicked(true);
    setSelectedTab(null);
  };

  const passkeyErrorNotice = () => {
    return (
      <div role="alert">
        {passkeyStore.status === FetchStatus.ERROR && isPasskeyPanel && (
          <>
            <Notice type={passkeyStore.errorType === PasskeyErrorType.USER_CANCELLED ? 'attention' : 'error'}>
              {translate(`passkey.error.${passkeyStore.errorType}`)}
            </Notice>
            <Separator color="invisible" spacing="xsmall" />
          </>
        )}
      </div>
    );
  };

  return (
    <WrapWithContainerInFullPage>
      {Config.isModule && <Separator spacing="medium" color="invisible" />}
      {passkeyErrorNotice()}
      <Card type="light" background="transparent" borderRadius="large">
        <CardContent>
          <Grid
            align={['middle-xs']}
            noWrap={true}
            horizontalGutter="small"
            verticalGutter="none"
            className={panel.id === 'admin' ? 'text-employee' : undefined}
          >
            <GridColumn width={['min']} align={['top-xs']}>
              <Icon modifiers={panel.icon} size="sm" />
            </GridColumn>
            <GridColumn width={['max']}>
              <Grid noWrap={true} verticalGutter="none" horizontalGutter="small" align={['middle-xs']}>
                <GridColumn width={['min']}>
                  <Heading level={2} className="h5">
                    {translate(`panel.${panel.id}.label`)}
                  </Heading>
                </GridColumn>
                {isPasskeyPanel && (
                  <GridColumn width={['max']}>
                    <Badge size="medium" purpose="recommended" text={translate('panel.badge.new')} />
                  </GridColumn>
                )}
              </Grid>
              {isPasskeyPanel && (
                <>
                  <p className="text-light">{translate('panel.passkey.subHeading')}</p>
                  <Separator color="invisible" spacing="xsmall" />
                  <p>{translate(`panel.passkey.explanation${passkeyExists ? '.exists' : ''}`)}</p>
                  <Button link={true} text={translate('panel.passkey.cta')} target="_blank" url={translate('panel.passkey.cta.url')} />
                </>
              )}
            </GridColumn>
          </Grid>
          <Separator spacing="small" color="invisible" />
          {selectedTab === panel.id && hasNotice && (
            <>
              <UiNotice />
              <Separator spacing="xxsmall" color="invisible" />
            </>
          )}
          <ChildComponent />
        </CardContent>
      </Card>
      {panels.length !== 1 && (
        <>
          <Separator spacing="small" color="invisible" />
          <Button text={translate('panel.otherLoginTypes')} link={true} iconLeft="#arrow-left" onClick={handleBackToMethodSelection} />
        </>
      )}
    </WrapWithContainerInFullPage>
  );
});

export default SelectedMobilePanel;
